"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { Loader2 } from "lucide-react"
import { getContacts, getConversation } from "./api"
import { generatePresignedUrl } from "../postFileAws"
import ConversationsList from "./ConversationsList"
import ChatArea from "./ChatArea"
import ProfileModal from "./ProfileModal"
import GroupInfoModal from "./GroupInfoModal"
import GroupCreationModal from "./GroupCreationModal"
import { requestNotificationPermission, saveTokenToDatabase } from "./fcm-api"
import { setupMessageHandler } from "../firebase-messaging"

// Lambda API URL for backend operations
const LAMBDA_API_URL = "https://tvaf32pm2tarajmrxaxyy3pp3e0zgjyx.lambda-url.ap-south-1.on.aws/"

// Enhanced CSS for mobile scrolling fixes and emoji visibility
const enhancedScrollbarStyles = `
  /* Enhanced scrollbar styles */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  }
  
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 20px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
  }

  /* Online status indicator styles */
  .online-indicator {
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 12px;
    height: 12px;
    background-color: #10b981;
    border: 2px solid white;
    border-radius: 50%;
    z-index: 10;
  }

  .online-indicator-small {
    width: 8px;
    height: 8px;
    border: 1px solid white;
  }

  .profile-container {
    position: relative;
    display: inline-block;
  }

  .online-status-text {
    color: #10b981;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .offline-status-text {
    color: #6b7280;
    font-size: 0.75rem;
  }

  /* MOBILE SCROLLING FIXES */
  @media (max-width: 768px) {
    /* Main container fixes */
    .mobile-chat-container {
      height: 100vh;
      height: 100dvh; /* Dynamic viewport height for mobile */
      overflow: hidden;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      touch-action: pan-y;
    }
    
    /* Sidebar scrolling fixes */
    .mobile-sidebar {
      height: 100vh;
      height: 100dvh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      touch-action: pan-y;
    }
    
    /* Chat messages scrolling fixes */
    .mobile-chat-messages {
      height: calc(100vh - 160px);
      height: calc(100dvh - 160px);
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      padding-bottom: 20px;
      touch-action: pan-y;
    }
    
    /* Contacts list scrolling fixes */
    .mobile-contacts-list {
      height: calc(100vh - 140px);
      height: calc(100dvh - 140px);
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      touch-action: pan-y;
    }
    
    /* Conversations list scrolling fixes */
    .mobile-conversations-list {
      height: calc(100vh - 180px);
      height: calc(100dvh - 180px);
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
      touch-action: pan-y;
    }
    
    /* Fix for iOS Safari viewport issues */
    .mobile-viewport-fix {
      min-height: -webkit-fill-available;
    }
    
    /* Prevent body scroll when chat is open */
    body.chat-open {
      overflow: hidden;
      position: fixed;
      width: 100%;
      height: 100%;
    }

    /* Mobile file name truncation */
    .mobile-file-name {
      max-width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* Mobile conversation text truncation */
    .mobile-conversation-text {
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  /* Profile click styles */
  .profile-clickable {
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 50%;
  }
  
  .profile-clickable:hover {
    opacity: 0.8;
    transform: scale(1.02);
  }
  
  .profile-clickable:active {
    opacity: 0.6;
    transform: scale(0.98);
  }
  
  .chat-header-profile-clickable {
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 8px;
    padding: 4px;
    margin: -4px;
  }
  
  .chat-header-profile-clickable:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  /* Group message sender name styling */
  .group-sender-name {
    font-size: 0.75rem;
    font-weight: 600;
    margin-bottom: 4px;
    color: #3b82f6;
    cursor: pointer;
    transition: color 0.2s ease;
  }

  .group-sender-name:hover {
    color: #1d4ed8;
  }

  /* Group info button styling */
  .group-info-button {
    padding: 4px;
    border-radius: 50%;
    transition: background-color 0.2s ease;
  }

  .group-info-button:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`

const formatMessageTimeForDisplay = (dateString) => {
  try {
    const date = new Date(dateString)

    // Format: "DD/MM/YYYY, hh:mm:ss am/pm"
    const day = date.getDate().toString().padStart(2, "0")
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const year = date.getFullYear()

    let hours = date.getHours()
    const minutes = date.getMinutes().toString().padStart(2, "0")
    const seconds = date.getSeconds().toString().padStart(2, "0")
    const ampm = hours >= 12 ? "pm" : "am"

    hours = hours % 12
    hours = hours ? hours : 12 // Convert 0 to 12
    const hoursStr = hours.toString().padStart(2, "0")

    return `${day}/${month}/${year}, ${hoursStr}:${minutes}:${seconds} ${ampm}`
  } catch (error) {
    console.error("Error formatting message time:", error)
    return ""
  }
}

const ChitChatMain = () => {
  // Core state
  const [conversations, setConversations] = useState([])
  const [activeView, setActiveView] = useState("messages")
  const [currentUser, setCurrentUser] = useState(null)
  const [contactsData, setContactsData] = useState([])
  const [ws, setWs] = useState(null)
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false)
  const [isCreatingGroup, setIsCreatingGroup] = useState(false)
  const [currentChatId, setCurrentChatId] = useState(null)
  const [isPageLoading, setIsPageLoading] = useState(true)
  const [isContactsLoading, setIsContactsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredConversations, setFilteredConversations] = useState([])
  const [notificationsEnabled, setNotificationsEnabled] = useState(false)
  const [fcmToken, setFcmToken] = useState(null)
  const [profileImageUrls, setProfileImageUrls] = useState({})

  // Modal states
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [showGroupInfoModal, setShowGroupInfoModal] = useState(false)
  const [selectedGroupInfo, setSelectedGroupInfo] = useState(null)

  // Mobile states
  const [isMobile, setIsMobile] = useState(false)
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false)

  // Online status tracking
  const [userOnlineStatus, setUserOnlineStatus] = useState({})

  // Typing functionality
  const [typingStatus, setTypingStatus] = useState({})

  // Refs
  const unsubscribeRef = useRef(null)
  const messageHandlerUnsubscribeRef = useRef(null)

  // Initialize current user
  useEffect(() => {
    if (typeof window !== "undefined") {
      // For students
      const studentData = localStorage.getItem("studentData")
      if (studentData) {
        const userDet = JSON.parse(studentData)
        setCurrentUser({
          pid: userDet._id,
          pName: userDet.studentName,
        })
        return
      }

      // For teachers
      const teacherData = localStorage.getItem("teacherData")
      if (teacherData) {
        const userDet = JSON.parse(teacherData)
        setCurrentUser({
          pid: userDet._id,
          pName: userDet.teacherName,
        })
        return
      }
    }
  }, [])

  // Enhanced mobile detection with viewport fixes
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth < 768
      setIsMobile(isMobileDevice)

      // Set CSS custom property for mobile viewport height
      if (isMobileDevice) {
        const vh = window.innerHeight * 0.01
        document.documentElement.style.setProperty("--vh", `${vh}px`)
        document.body.classList.add("chat-open")
      } else {
        document.body.classList.remove("chat-open")
      }
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)
    window.addEventListener("orientationchange", () => {
      setTimeout(checkMobile, 100)
    })

    return () => {
      window.removeEventListener("resize", checkMobile)
      window.removeEventListener("orientationchange", checkMobile)
      document.body.classList.remove("chat-open")
    }
  }, [])

  // Function to set user online status via WebSocket
  const setOnlineStatus = (isOnline = true) => {
    if (!ws || !currentUser?.pid) return

    try {
      ws.send(
        JSON.stringify({
          action: isOnline ? "setOnline" : "setOffline",
          pid: currentUser.pid,
          pName: currentUser.pName,
        }),
      )
      console.log(`Sent ${isOnline ? "setOnline" : "setOffline"} via WebSocket for user ${currentUser.pid}`)
    } catch (error) {
      console.error(`WebSocket error sending ${isOnline ? "setOnline" : "setOffline"}:`, error)
    }
  }

  // Function to check if a user is online
  const isUserOnline = (pid) => {
    if (!pid) return false
    return userOnlineStatus[pid] === true
  }

  // Handle page visibility change to set online/offline status
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setOnlineStatus(false)
      } else {
        setOnlineStatus(true)
      }
    }

    const handleBeforeUnload = () => {
      setOnlineStatus(false)
    }

    const handleFocus = () => {
      setOnlineStatus(true)
    }

    const handleBlur = () => {
      setOnlineStatus(false)
    }

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange)
    window.addEventListener("beforeunload", handleBeforeUnload)
    window.addEventListener("focus", handleFocus)
    window.addEventListener("blur", handleBlur)

    // Set online when component mounts
    if (currentUser?.pid) {
      setOnlineStatus(true)
    }

    // Cleanup function
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      window.removeEventListener("beforeunload", handleBeforeUnload)
      window.removeEventListener("focus", handleFocus)
      window.removeEventListener("blur", handleBlur)

      // Set offline when component unmounts
      if (currentUser?.pid) {
        setOnlineStatus(false)
      }
    }
  }, [currentUser?.pid])

  // Function to call the Lambda API for setSeen
  const setSeenViaAPI = async (chatId, userId) => {
    try {
      const response = await fetch(LAMBDA_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          request: "setSeen",
          body: {
            gid: chatId,
            pid: userId,
          },
        }),
      })

      if (!response.ok) {
        throw new Error(`API call failed with status: ${response.status}`)
      }

      const data = await response.json()
      console.log("setSeen API response:", data)
      return data
    } catch (error) {
      console.error("Error calling setSeen API:", error)
      return null
    }
  }

  const loadProfileImages = async (conversations) => {
    try {
      const imageUrls = { ...profileImageUrls }
      let hasNewImages = false

      for (const chat of conversations) {
        if (!chat.participants) continue

        for (const participant of chat.participants) {
          if (participant.profilePhotoS3Uri && !imageUrls[participant.pid]) {
            try {
              console.log("Generating presigned URL for:", participant.pid, participant.pName)
              const presignedUrl = await generatePresignedUrl(participant.profilePhotoS3Uri)
              imageUrls[participant.pid] = presignedUrl
              hasNewImages = true
            } catch (error) {
              console.error("Error generating presigned URL:", error)
            }
          }
        }
      }

      if (hasNewImages) {
        console.log("Updated profile image URLs:", imageUrls)
        setProfileImageUrls(imageUrls)
      }
    } catch (error) {
      console.error("Error loading profile images:", error)
    }
  }

  // Function to update online status from conversations
  const updateOnlineStatusFromConversations = useCallback(
    (conversations) => {
      const statusMap = {}

      conversations.forEach((conversation) => {
        if (conversation.participants) {
          conversation.participants.forEach((participant) => {
            if (participant.pid !== currentUser?.pid) {
              statusMap[participant.pid] = participant.isOnline === true
            }
          })
        }
      })

      setUserOnlineStatus(statusMap)
    },
    [currentUser?.pid],
  )

  // Function to get all conversations - now using useCallback
  const getAllConversations = useCallback(
    async (userId) => {
      setIsPageLoading(true)
      try {
        const conversationsArr = await getConversation(userId)

        // Sort conversations by latestMessageTimeFormatted
        const sortedConversations = [...conversationsArr].sort((a, b) => {
          const parseFormattedTime = (timeStr) => {
            try {
              if (!timeStr || typeof timeStr !== "string") {
                return null
              }

              const parts = timeStr.split(", ")
              if (parts.length !== 2) {
                return null
              }

              const [datePart, timePart] = parts
              const dateParts = datePart.split("/")
              if (dateParts.length !== 3) {
                return null
              }

              const [day, month, year] = dateParts
              const timePeriodParts = timePart.split(" ")
              if (timePeriodParts.length !== 2) {
                return null
              }

              const [time, period] = timePeriodParts
              const timeParts = time.split(":")
              if (timeParts.length < 2) {
                return null
              }

              let [hours, minutes, seconds = "00"] = timeParts

              if (period.toLowerCase() === "pm" && hours !== "12") {
                hours = String(Number.parseInt(hours) + 12)
              } else if (period.toLowerCase() === "am" && hours === "12") {
                hours = "00"
              }

              const dateString = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}:${seconds.padStart(2, "0")}`
              const result = new Date(dateString)
              return isNaN(result.getTime()) ? null : result
            } catch (error) {
              console.error("Error parsing formatted time:", error)
              return null
            }
          }

          const aFormattedTime = parseFormattedTime(a.latestMessageTimeFormatted)
          const bFormattedTime = parseFormattedTime(b.latestMessageTimeFormatted)

          if (aFormattedTime && bFormattedTime) {
            return bFormattedTime.getTime() - aFormattedTime.getTime()
          }

          if (aFormattedTime && !bFormattedTime) return -1
          if (!aFormattedTime && bFormattedTime) return 1

          if (a.latestMessageTime && b.latestMessageTime) {
            const aTime = new Date(a.latestMessageTime)
            const bTime = new Date(b.latestMessageTime)
            return bTime.getTime() - aTime.getTime()
          }

          const aHasLatestMessage = !!a.latestMessage
          const bHasLatestMessage = !!b.latestMessage

          if (aHasLatestMessage && !bHasLatestMessage) return -1
          if (!aHasLatestMessage && bHasLatestMessage) return 1

          const aTime = new Date(a.updatedAt || a.createdAt)
          const bTime = new Date(b.updatedAt || b.createdAt)
          return bTime.getTime() - aTime.getTime()
        })

        setConversations(sortedConversations)
        loadProfileImages(sortedConversations)
        updateOnlineStatusFromConversations(sortedConversations)
      } catch (error) {
        console.error("Error fetching conversations:", error)
      } finally {
        setIsPageLoading(false)
      }
    },
    [updateOnlineStatusFromConversations],
  )

  // Set up message handler
  useEffect(() => {
    let unsubscribe

    if (currentUser?.pid) {
      unsubscribe = setupMessageHandler((payload) => {
        console.log("Message received in ChitChat component:", payload)

        if (payload.data && payload.data.gid) {
          const chatId = payload.data.gid
          getAllConversations(currentUser.pid)
        }
      })

      messageHandlerUnsubscribeRef.current = unsubscribe
    }

    return () => {
      if (unsubscribe) {
        unsubscribe()
        console.log("ChitChat component unmounting and unsubscribing from message handler")
      }
    }
  }, [currentUser?.pid, currentChatId, getAllConversations])

  // Load contacts and conversations
  useEffect(() => {
    async function getAllContacts(userId) {
      setIsContactsLoading(true)
      try {
        const response = await getContacts(userId)

        let parsedContacts
        if (typeof response === "string") {
          try {
            parsedContacts = JSON.parse(response)
          } catch (e) {
            console.error("Error parsing contacts response:", e)
            parsedContacts = []
          }
        } else {
          parsedContacts = response
        }

        console.log("Contacts data:", parsedContacts)
        setContactsData(parsedContacts)
      } catch (error) {
        console.error("Error fetching contacts:", error)
        setContactsData([])
      } finally {
        setIsContactsLoading(false)
      }
    }

    if (currentUser) {
      getAllContacts(currentUser.pid)
      getAllConversations(currentUser.pid)
    }
  }, [currentUser, getAllConversations])

  // WebSocket setup
  useEffect(() => {
    if (currentUser?.pid) {
      const ws = new WebSocket("wss://agwbedbhbi.execute-api.ap-south-1.amazonaws.com/development/")

      ws.onopen = () => {
        console.log("WebSocket connected")
        if (currentUser?.pid) {
          ws.send(
            JSON.stringify({
              action: "setId",
              pid: currentUser.pid,
            }),
          )

          setTimeout(() => {
            setOnlineStatus(true)
          }, 100)
        }

        setWs(ws)
      }

      ws.onmessage = (e) => {
        const event = JSON.parse(e.data)
        console.log("WebSocket message received:", event)

        // Handle online status updates
        if (event.message && event.message.action === "onlineStatusUpdate") {
          const { pid, isOnline } = event.message
          console.log(`Received online status update: ${pid} is ${isOnline ? "online" : "offline"}`)

          setUserOnlineStatus((prev) => ({
            ...prev,
            [pid]: isOnline,
          }))

          setConversations((prevConversations) =>
            prevConversations.map((conversation) => ({
              ...conversation,
              participants: conversation.participants?.map((participant) =>
                participant.pid === pid ? { ...participant, isOnline } : participant,
              ),
            })),
          )
          return
        }

        // Handle typing status updates
        if (event.message && event.message.action === "typingUpdate") {
          const { gid, pid, pName, isTyping } = event.message

          setTypingStatus((prev) => ({
            ...prev,
            [gid]: {
              ...prev[gid],
              [pid]: isTyping ? { isTyping, pName } : undefined,
            },
          }))
        } else if (event.message && event.message.action === "sendMessage") {
          const { gid, pid, pName, messageType, mediaUrl, message } = event.message

          // FIXED: Only update latestMessage for actual content messages (text/file)
          // Skip reactions, deletions, and other non-content message types
          const shouldUpdateLatestMessage = messageType === "text" || messageType === "file"

          console.log("Processing message:", { messageType, shouldUpdateLatestMessage, message })

          // Handle various message types and update conversations
          setConversations((prevConversations) => {
            const updatedChat = prevConversations.find((chat) => chat._id === gid)

            if (!updatedChat) {
              // Only create new conversation for actual content messages
              if (!shouldUpdateLatestMessage) {
                return prevConversations
              }

              // Create new conversation for incoming message
              const newChat = {
                _id: gid,
                chatType: "pTp",
                participants: [
                  {
                    pid: currentUser?.pid || "",
                    pName: currentUser?.pName || "",
                    seen: gid === currentChatId ? "y" : "n",
                    unseenCounter: 0,
                  },
                  {
                    pid,
                    pName,
                    seen: "y",
                    unseenCounter: pid === currentUser?.pid ? 0 : 1,
                  },
                ],
                latestMessage: message,
                latestMessageTime: event.message.createdAt || new Date().toISOString(),
                latestMessageTimeFormatted: formatMessageTimeForDisplay(event.message.createdAt || new Date()),
                latestMessageSendBy: {
                  pid: pid,
                  pName: pName,
                },
                updatedAt: event.message.updatedAt || event.message.createdAt || new Date().toISOString(),
                createdAt: event.message.createdAt || new Date().toISOString(),
              }

              return [newChat, ...prevConversations]
            }

            // FIXED: Only update conversation details for actual content messages
            if (shouldUpdateLatestMessage) {
              const updatedChatWithMessage = {
                ...updatedChat,
                latestMessage: message,
                updatedAt: event.message.updatedAt || event.message.createdAt || updatedChat.updatedAt,
                latestMessageTime: event.message.createdAt || new Date().toISOString(),
                latestMessageTimeFormatted: formatMessageTimeForDisplay(event.message.createdAt || new Date()),
                latestMessageSendBy: {
                  pid: pid,
                  pName: pName,
                },
                participants: updatedChat.participants?.map((p) => {
                  if (p.pid === currentUser?.pid) {
                    return { ...p, seen: gid === currentChatId ? "y" : "n", unseenCounter: 0 }
                  } else if (p.pid === pid) {
                    return { ...p, seen: "y", unseenCounter: 0 }
                  } else {
                    return {
                      ...p,
                      unseenCounter: pid !== p.pid ? (p.unseenCounter || 0) + 1 : p.unseenCounter || 0,
                      seen: "n",
                    }
                  }
                }),
              }

              const filteredConversations = prevConversations.filter((chat) => chat._id !== gid)
              return [updatedChatWithMessage, ...filteredConversations]
            } else {
              // For reactions, deletions, etc., just update participant status without changing latest message
              const updatedChatWithoutMessageChange = {
                ...updatedChat,
                participants: updatedChat.participants?.map((p) => {
                  if (p.pid === currentUser?.pid) {
                    return { ...p, seen: gid === currentChatId ? "y" : "n" }
                  }
                  return p
                }),
              }

              const filteredConversations = prevConversations.filter((chat) => chat._id !== gid)
              return [updatedChatWithoutMessageChange, ...filteredConversations]
            }
          })
        } else if (event.message && (event.message.chatType === "pTe" || event.message.chatType === "pTp")) {
          const newChat = {
            _id: event.message._id,
            chatType: event.message.chatType,
            participants: event.message.participants,
            latestMessage: null,
            createdAt: event.message.createdAt,
            updatedAt: event.message.updatedAt,
            __v: event.message.__v,
          }

          // Check if this is replacing a temporary chat
          const tempChatToReplace = conversations.find(
            (chat) =>
              chat._id.startsWith("temp_") &&
              chat.chatType === newChat.chatType &&
              chat.participants?.some((p1) =>
                newChat.participants?.some((p2) => p1.pid === p2.pid && p1.pid !== currentUser?.pid),
              ),
          )

          if (tempChatToReplace) {
            // Replace the temporary chat with the real one
            setConversations((prevConversations) =>
              prevConversations.map((chat) => (chat._id === tempChatToReplace._id ? newChat : chat)),
            )

            // Update the current chat ID if we were viewing the temp chat
            if (currentChatId === tempChatToReplace._id) {
              setCurrentChatId(newChat._id)
            }
          } else {
            // Add new chat normally
            setConversations((prevConversations) => [newChat, ...prevConversations])
          }

          setIsCreatingGroup(false)
          setIsGroupModalOpen(false)
          setActiveView("messages")
        }
      }

      ws.onclose = () => {
        console.log("WebSocket disconnected")
        setOnlineStatus(false)
      }

      ws.onerror = (error) => {
        console.error("WebSocket error:", error)
      }

      return () => {
        if (ws) {
          setOnlineStatus(false)
          ws.close()
        }
      }
    }
  }, [currentUser?.pid, currentChatId, setConversations, getAllConversations])

  useEffect(() => {
    if (conversations.length > 0) {
      loadProfileImages(conversations)
    }
  }, [conversations])

  const filterConversations = (searchTerm) => {
    if (!searchTerm.trim()) return conversations
    return conversations.filter((chat) => {
      const chatName = getChatName(chat).toLowerCase()
      if (chatName.includes(searchTerm.toLowerCase())) return true

      if (chat.latestMessage && chat.latestMessage.toLowerCase().includes(searchTerm.toLowerCase())) return true

      return false
    })
  }

  useEffect(() => {
    setFilteredConversations(filterConversations(searchTerm))
  }, [searchTerm, conversations])

  const getChatName = (chat) => {
    if (!chat) return "Unknown Chat"

    if (chat.chatType === "pTe") {
      return chat.chatName || "Group Chat"
    } else if (chat.chatType === "pTp") {
      const otherParticipant = chat.participants?.find((p) => p.pid !== currentUser?.pid)
      return otherParticipant?.pName || "Unknown Contact"
    } else {
      return "Unknown Chat"
    }
  }

  // Handle enabling notifications
  const handleEnableNotifications = async () => {
    if (!notificationsEnabled && currentUser) {
      const token = await requestNotificationPermission()
      if (token) {
        setFcmToken(token)
        setNotificationsEnabled(true)
        await saveTokenToDatabase(currentUser.pid, token)
      }
    }
  }

  const handleCreateGroup = (groupName, participants) => {
    if (!currentUser) return

    setIsCreatingGroup(true)
    const groupCreationMessage = {
      action: "setGroup",
      chatType: "pTe",
      chatName: groupName,
      participants: [
        { pid: currentUser.pid, pName: currentUser.pName, unseenCounter: 0 },
        ...participants?.map((p) => ({ pid: p.id, pName: p.name, unseenCounter: 0 })),
      ],
    }

    if (ws) {
      ws.send(JSON.stringify(groupCreationMessage))
    } else {
      console.error("WebSocket connection is not available")
      setIsCreatingGroup(false)
      setIsGroupModalOpen(false)
    }
  }

  // Function to handle profile click
  const handleProfileClick = (participant) => {
    console.log("handleProfileClick called with:", participant)
    if (participant) {
      setSelectedProfile({
        ...participant,
        profileImageUrl: profileImageUrls[participant.pid] || null,
      })
      setShowProfileModal(true)
      console.log("Profile modal should open now")
    }
  }

  // Function to handle group info click
  const handleGroupInfoClick = (chat) => {
    setSelectedGroupInfo(chat)
    setShowGroupInfoModal(true)
  }

  if (isPageLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-100 to-blue-50">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto mb-4">
            <Loader2 className="h-full w-full animate-spin text-blue-500" />
          </div>
          <p className="text-lg font-medium text-gray-700">Loading ChitChat...</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`bg-gradient-to-br from-blue-100 to-blue-50 ${isMobile && isMobileChatOpen ? "fixed inset-0 z-50 bg-white" : "h-screen"} overflow-hidden chat-container`}
    >
      <style jsx>{enhancedScrollbarStyles}</style>

      <div className="flex md:flex-row h-full font-sans">
        {/* Conversations List Sidebar */}
        <ConversationsList
          conversations={conversations}
          setConversations={setConversations}
          filteredConversations={filteredConversations}
          activeView={activeView}
          setActiveView={setActiveView}
          contactsData={contactsData}
          isContactsLoading={isContactsLoading}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          notificationsEnabled={notificationsEnabled}
          handleEnableNotifications={handleEnableNotifications}
          currentUser={currentUser}
          currentChatId={currentChatId}
          setCurrentChatId={setCurrentChatId}
          isMobile={isMobile}
          isMobileChatOpen={isMobileChatOpen}
          setIsMobileChatOpen={setIsMobileChatOpen}
          profileImageUrls={profileImageUrls}
          isUserOnline={isUserOnline}
          typingStatus={typingStatus}
          getChatName={getChatName}
          setSeenViaAPI={setSeenViaAPI}
          handleProfileClick={handleProfileClick}
          handleGroupInfoClick={handleGroupInfoClick}
          setIsGroupModalOpen={setIsGroupModalOpen}
          isCreatingGroup={isCreatingGroup}
        />

        {/* Chat Area */}
        <ChatArea
          currentChatId={currentChatId}
          setCurrentChatId={setCurrentChatId}
          conversations={conversations}
          setConversations={setConversations}
          currentUser={currentUser}
          ws={ws}
          isMobile={isMobile}
          isMobileChatOpen={isMobileChatOpen}
          setIsMobileChatOpen={setIsMobileChatOpen}
          profileImageUrls={profileImageUrls}
          isUserOnline={isUserOnline}
          typingStatus={typingStatus}
          setTypingStatus={setTypingStatus}
          getChatName={getChatName}
          setSeenViaAPI={setSeenViaAPI}
          handleProfileClick={handleProfileClick}
          handleGroupInfoClick={handleGroupInfoClick}
          formatMessageTimeForDisplay={formatMessageTimeForDisplay}
        />
      </div>

      {/* Group Creation Modal */}
      {isGroupModalOpen && (
        <GroupCreationModal
          isOpen={isGroupModalOpen}
          onClose={() => setIsGroupModalOpen(false)}
          contacts={contactsData}
          onCreateGroup={handleCreateGroup}
          isCreating={isCreatingGroup}
        />
      )}

      {/* Profile Modal */}
      {showProfileModal && selectedProfile && (
        <ProfileModal
          isOpen={showProfileModal}
          onClose={() => {
            console.log("Closing profile modal")
            setShowProfileModal(false)
            setSelectedProfile(null)
          }}
          participant={selectedProfile}
          profileImageUrls={profileImageUrls}
        />
      )}

      {/* Group Info Modal */}
      {showGroupInfoModal && selectedGroupInfo && (
        <GroupInfoModal
          isOpen={showGroupInfoModal}
          onClose={() => setShowGroupInfoModal(false)}
          groupInfo={selectedGroupInfo}
          profileImageUrls={profileImageUrls}
          userOnlineStatus={userOnlineStatus}
          currentUserId={currentUser?.pid}
        />
      )}
    </div>
  )
}

export default ChitChatMain
